import { Component, ElementRef, Input, OnDestroy, Optional, Self, OnInit, Output, EventEmitter, ViewChild, HostListener, DoCheck } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ControlValueAccessor, NgControl} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatFormFieldControl } from '@angular/material/form-field';
import * as CKEditor from './ckeditor';
import { Subject } from 'rxjs';

@Component({
// tslint:disable-next-line: component-selector
	selector: 'tr2-editor',
	templateUrl: 'editor.component.html',
	styleUrls: ['editor.component.scss'],
	providers: [
		{ provide: MatFormFieldControl, useExisting: Editor }
	],
	/** Definimos atributos para el html del componente */
	host: {
		'[class.editor-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'[attr.aria-describedby]': 'describedBy'
	}
})
export class Editor implements MatFormFieldControl<string>, OnDestroy, ControlValueAccessor, OnInit, DoCheck {

	/** Variable que setea el placeholder de la entrada */
	@Input()
	get placeholder(): string {
		return this._placeholder;
	}
	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}

	/** Variable que asigna el atributo name al input */
	@Input()
	get name(): any[] {
		return this._name;
	}
	set name(value: any[]) {
		this._name = value;
		this.stateChanges.next();
	}

	/** Variable que indica si el campo es requerido para el formulario */
	@Input()
	get required(): boolean {
		if (this._required) {
			return this._required;
		}

		// cuando el control retorna un error desde la validación con un valor vacío
		if (this.ngControl && this.ngControl.control && this.ngControl.control.validator) {
			const emptyValueControl = Object.assign({}, this.ngControl.control);
			(emptyValueControl as any).value = null;
			return 'required' in (this.ngControl.control.validator(emptyValueControl) || {});
		}
		return false;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}

	/** Variable para setear si el campo de entrada está deshabilitado o no */
	@Input()
	get disabled(): boolean {
		if (this.ngControl && this.ngControl.control && this.ngControl.control.disabled) {
			this._disabled = this.ngControl.control.disabled;
		}
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.form.disable() : this.form.enable();
		this.stateChanges.next();
	}

	/** Variable en donde se captura el valor de la entrada */
	@Input()
	get value(): any {
		return this.control.value;
	}
	set value(val: any) {
		this.control.setValue(val);
		this.stateChanges.next();
	}

	/** Función que indica si la entrada del formulario esta vacía */
	get empty() {
		return !this.control || this.control.value === null || this.control.value === '';
	}

	/** Función que indica si la etiqueta de la entrada pasa a flotante o no */
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	/** Constructor de la clase */
	constructor(
		fb: FormBuilder,
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl,
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		fm.monitor(elRef, true).subscribe(origin => {
			this.focused = !!origin;
			this.stateChanges.next();
		});
	}

	/****** Definimos variables de clase ******/
	static nextId = 0;
	form: FormGroup;
	stateChanges = new Subject<void>();
	focused = false;
	errorState = false;
	controlType = 'tr2-editor';
	id = `tr2-editor-${Editor.nextId++}`;
	describedBy = '';
	control = new FormControl(null);
	readonly = false;
	private _placeholder: string;
	private _name: any[];
	private _required = false;
	private _disabled = false;
	public Editor = CKEditor;

	/** configuración del editor */
	@Input() config = {

		// ESTA COMENTADO PERO ANDA!!!!
		// Sirve para configurar las opciones de font-family, los tamaños y colores disponibles para el usuario

		// fontFamily: {
		// 	options: [
		// 		'default',
		// 		'Ubuntu, Arial, sans-serif',
		// 		'Ubuntu Mono, Courier New, Courier, monospace'
		// 	]
		// },
		// fontSize: {
        //     options: [
        //         9,
        //         11,
        //         13,
        //         'default',
        //         17,
        //         19,
        //         21
        //     ]
		// },
		// fontColor: {
        //     colors: [
        //         {
        //             color: 'hsl(0, 0%, 0%)',
        //             label: 'Black'
        //         },
        //         {
        //             color: 'hsl(0, 0%, 30%)',
        //             label: 'Dim grey'
        //         },
        //         {
        //             color: 'hsl(0, 0%, 60%)',
        //             label: 'Grey'
        //         },
        //         {
        //             color: 'hsl(0, 0%, 90%)',
        //             label: 'Light grey'
        //         },
        //         {
        //             color: 'hsl(0, 0%, 100%)',
        //             label: 'White',
        //             hasBorder: true
        //         },
        //     ]
        // },
		// fontBackgroundColor: {
        //     colors: [
        //         {
        //             color: 'hsl(0, 75%, 60%)',
        //             label: 'Red'
        //         },
        //         {
        //             color: 'hsl(30, 75%, 60%)',
        //             label: 'Orange'
        //         },
        //         {
        //             color: 'hsl(60, 75%, 60%)',
        //             label: 'Yellow'
        //         },
        //         {
        //             color: 'hsl(90, 75%, 60%)',
        //             label: 'Light green'
        //         },
        //         {
        //             color: 'hsl(120, 75%, 60%)',
        //             label: 'Green'
        //         }
        //     ]
		// },
		placeholder: 'Escriba aquí...',
		toolbar: [ 'undo',
		'redo',
		'|',
		'heading',
		'|',
		'fontFamily',
		'fontSize',
		'fontColor',
		'fontBackgroundColor',
		'|',
		'bold',
		'italic',
		'bulletedList',
		'numberedList',
		'|',
		'indent',
		'outdent' ]
	};

	/** Método que emite evento al cambiar el editor */
	// @Output() cambiaEditor = new EventEmitter();

	/****** Métodos de clase ******/
	ngOnInit() {
		// setemos y enlazamos el formControl al del input del autocomplete
		this.control = this.ngControl.control as FormControl;
		console.log(this.Editor);
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef);
	}

	ngDoCheck() {
		this.errorState = this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty);
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	onContainerClick(event: MouseEvent) {

	}

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	writeValue() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnChange() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnTouched() { }

	limpiar() {
		this.control.reset();
		// this.cambiaEditor.emit(this.control.value);
	}

	check() {
		if (this.control && (this.control.invalid || !this.control.value)) {
			// se deberia vaciar el contenido, ya veremos como
		}
	}

	focus() {
		// aca hacemos foco, pero no sabemos como todavia
	}

	onFocus(event) {
		if (event.target.select) {
			event.target.select();
		}
	}

	onBlur(event) {
		this.check();
		this.setTouched();
	}

	/** Método que hace que aparezca el mat-error correctamente */
	setTouched(): void {
		if (this.control && !this.control.touched) {
			this.control.markAsTouched();
			this.errorState = this.control.invalid;
		}
		this.stateChanges.next();
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.keyCode == 27) {
			event.preventDefault();
			event.stopPropagation();
			this.limpiar();
		}
	}
}
